import React, { FunctionComponent } from "react";
import { Dialog, IBannerContext, BannerStatus } from "@plex/react-components";
import { IViewController, useViewController } from "../ViewContext";
import "./UserDefinedSchemaEditor.scss";
import {
  IUserDefinedSchemaPropertyDetailsFormState,
  UserDefinedSchemaPropertyDetailsForm
} from "./UserDefinedSchemaPropertyDetailsForm";
import {
  addObjectListSchemaWithItemSchema,
  getChildSchemaIdsRecursive,
  getDataTypeFromSchema,
  getListSchemaWithItemSchema,
  getSchema,
  getSchemaId,
  getSchemas
} from "../NodeTypes/DataSchemas";
import {
  DocumentSchemaType,
  getSchemaObjectName,
  IDocumentListSchema,
  IDocumentObjectSchema,
  IDocumentObjectSchemaProperty
} from "../FlowDocument/FlowDocumentModel";
import { DataType, isPrimitiveLike } from "../NodeTypes/TypeDefinitions";
import { useReactFlow, useUpdateNodeInternals } from "reactflow";
import { refreshNodeDataPropertiesFromSchema } from "../Util/NodeUtil";
import { deleteEdgesFromSchemaProperty } from "../Util/EdgeUtil";

export interface IUserDefinedSchemaPropertyDetailsDialogState {
  show: boolean;
  schemaId: string;
  propertyName: string;
}

export interface IUserDefinedSchemaPropertyDialogProps {
  onClose?: () => void;
}

export const UserDefinedSchemaPropertyDetailsDialog: FunctionComponent<IUserDefinedSchemaPropertyDialogProps> = (
  props
) => {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const {
    userDefinedSchemaDetailsDialogState,
    userDefinedSchemaPropertyDetailsDialogState,
    setUserDefinedSchemaPropertyDetailsDialogState
  }: IViewController = useViewController();
  const schema = getSchema(userDefinedSchemaPropertyDetailsDialogState.schemaId);
  const schemaPropertyName = schema
    ? Object.keys((schema as unknown as IDocumentObjectSchema).properties).find(
        (propName: string) => propName === userDefinedSchemaPropertyDetailsDialogState.propertyName
      )
    : undefined;
  const schemaProperty = schemaPropertyName
    ? (schema as unknown as IDocumentObjectSchema).properties[schemaPropertyName]
    : undefined;
  const emptyInitialState: IUserDefinedSchemaPropertyDetailsFormState = {
    name: "",
    type: "string",
    required: false,
    schemaId: undefined,
    isList: false
  };
  let initialFormState = emptyInitialState;
  if (schemaProperty) {
    const propertySchema = getSchema(schemaProperty.schema);
    let dataType: string;
    if (propertySchema.schemaType === DocumentSchemaType.list) {
      dataType = getDataTypeFromSchema((propertySchema as unknown as IDocumentListSchema).listItemSchema);
    } else {
      dataType = getDataTypeFromSchema(schemaProperty.schema);
    }
    initialFormState = {
      name: schemaPropertyName!,
      type: dataType,
      required: schemaProperty.required,
      schemaId: isPrimitiveLike(dataType as DataType) ? undefined : schemaProperty.schema,
      schemaName: isPrimitiveLike(dataType as DataType) ? undefined : getSchemaObjectName(propertySchema),
      isList: propertySchema.schemaType === DocumentSchemaType.list
    };
  }

  const onSave = (formState: IUserDefinedSchemaPropertyDetailsFormState, bctx: IBannerContext) => {
    const propertyName = userDefinedSchemaPropertyDetailsDialogState.propertyName;
    let schema = getSchema(userDefinedSchemaPropertyDetailsDialogState.schemaId) as unknown as IDocumentObjectSchema;
    let propertySchemaId: string | undefined = formState.schemaId ?? getSchemaId(formState.type);
    if (formState.isList) {
      let listSchema = getListSchemaWithItemSchema(propertySchemaId!);

      // Create list schema if none exists for this schema
      if (!listSchema) {
        const itemSchema = getSchema(propertySchemaId!);
        addObjectListSchemaWithItemSchema(itemSchema);
        listSchema = getListSchemaWithItemSchema(propertySchemaId!);
      }

      if (listSchema) {
        propertySchemaId = listSchema.id;
      }
    }

    const oldSchemaId = schema.properties[propertyName]?.schema;
    const oldPropertyDataType = oldSchemaId ? (getDataTypeFromSchema(oldSchemaId) as DataType) : undefined;
    schema.properties[propertyName] = {
      schema: propertySchemaId,
      required: userDefinedSchemaDetailsDialogState.loopVariable ? true : formState.required
    } as IDocumentObjectSchemaProperty;

    if (oldSchemaId) {
      const getChildSchemasResult = getChildSchemaIdsRecursive(schema.id, getSchemas(), []);
      if (getChildSchemasResult.hasChildSchemaCycle) {
        bctx.addMessage(
          "Selecting this schema would create a cycle. Please choose a different schema.",
          BannerStatus.warning
        );
        schema.properties[propertyName]!.schema = oldSchemaId;
        return;
      }
    }

    const newPropertyDataType = getDataTypeFromSchema(schema.properties[propertyName]!.schema!) as DataType;
    if (oldPropertyDataType && oldPropertyDataType !== newPropertyDataType) {
      deleteEdgesFromSchemaProperty(
        schema.id,
        userDefinedSchemaDetailsDialogState.selectedPropertyName!,
        oldPropertyDataType,
        reactFlowInstance,
        updateNodeInternals
      );
    }
    closeDialog();
    refreshNodeDataPropertiesFromSchema(schema.id, reactFlowInstance, updateNodeInternals);
  };

  const closeDialog = () => {
    userDefinedSchemaPropertyDetailsDialogState.show = false;
    setUserDefinedSchemaPropertyDetailsDialogState({
      ...userDefinedSchemaPropertyDetailsDialogState
    });
  };

  return (
    <Dialog
      show={userDefinedSchemaPropertyDetailsDialogState.show}
      onHide={props.onClose}
      title="User Defined Schema"
      className="user-defined-schema-dialog"
    >
      <div style={{ height: "auto" }}>
        <UserDefinedSchemaPropertyDetailsForm
          userDefinedSchemaId={userDefinedSchemaPropertyDetailsDialogState.schemaId}
          initialState={initialFormState}
          onCancel={closeDialog}
          onSave={onSave}
        />
      </div>
    </Dialog>
  );
};
